import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  map,
  tap,
  take,
  skip
} from 'rxjs/operators';

import { IAPIFetchRequestOptions, IAPIListRequestOptions } from '@sondermind/utilities/models-http';

import { ClientReferralMembersHttpService } from '../services/client-referral-members.httpservice';
import { ClientReferralMember } from '../models/client-referral-member.model';

/**
 * Store for managing client referral members data
 */
@Injectable({
  providedIn: 'root'
})
export class ClientReferralMembersStore {
  private membersSubject = new BehaviorSubject<Map<number, ClientReferralMember>>(new Map());
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  /**
   * Observable of all cached client referral members as a Map
   */
  members$ = this.membersSubject.asObservable();

  /**
   * Observable indicating if data is currently being loaded
   */
  isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private clientReferralMembersHttpService: ClientReferralMembersHttpService
  ) {}

  /**
   * Loads a specific client referral member by ID
   * @param id The ID of the client referral member to load
   * @param forceRefresh Whether to force refresh the data even if already cached
   * @param opts Optional request options
   * @returns Observable of ClientReferralMember
   */
  loadMember$(
    id: number,
    forceRefresh: boolean = false,
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<ClientReferralMember> {
    const cached = this.membersSubject.value.get(id);

    if (forceRefresh || !cached) {
      this.isLoadingSubject.next(true);

      return this.clientReferralMembersHttpService.fetchById(id, opts).pipe(
        tap((member) => {
          const currentMembers = new Map(this.membersSubject.value);
          currentMembers.set(id, member);
          this.membersSubject.next(currentMembers);
          this.isLoadingSubject.next(false);
        })
      );
    }

    return this.members$.pipe(
      map((members) => members.get(id)!),
      take(1)
    );
  }

  /**
   * Refreshes a specific member's data and updates the store
   * @param id The ID of the client referral member to refresh
   * @param opts Optional request options
   * @returns Observable of ClientReferralMember
   */
  refreshMember(id: number, opts: Partial<IAPIFetchRequestOptions> = {}): Observable<ClientReferralMember> {
    this.isLoadingSubject.next(true);

    this.clientReferralMembersHttpService.fetchById(id, opts).pipe(
      take(1),
    ).subscribe((member) => {
      const currentMembers = new Map(this.membersSubject.value);
      currentMembers.set(id, member);
      this.membersSubject.next(currentMembers);
      this.isLoadingSubject.next(false);
    });

    return this.members$.pipe(
      skip(1),
      map((members) => members.get(id)!),
      take(1)
    );
  }

  /**
   * Gets a specific member by ID from the cache
   * @param id The ID of the member to find
   * @returns Observable of ClientReferralMember or null if not found
   */
  getMemberById$(id: number): Observable<ClientReferralMember | null> {
    return this.members$.pipe(
      map((members) => members.get(id) || null)
    );
  }

  /**
   * Clears all cached member data
   */
  clearCache(): void {
    this.membersSubject.next(new Map());
  }

  /**
   * Removes a specific member from the cache
   * @param id The ID of the member to remove
   */
  removeMemberFromCache(id: number): void {
    const currentMembers = new Map(this.membersSubject.value);
    currentMembers.delete(id);
    this.membersSubject.next(currentMembers);
  }

  /**
   * Update multiple members in the cache (batched update)
   * @param updatedCrms Array of members to update
   */
  updateMembersInCache(updatedCrms: ClientReferralMember[]): void {
    const currentMembers = new Map(this.membersSubject.value);
    updatedCrms.forEach((crm) => {
      currentMembers.set(crm.id, crm);
    });
    this.membersSubject.next(currentMembers);
  }

  /**
   * Gets all client referral members across all referrals
   * @param forceRefresh Whether to force refresh the data
   * @param opts Optional request options
   * @returns Observable of all ClientReferralMembers
   */
  getAll$(
    forceRefresh: boolean = false,
    opts: Partial<IAPIListRequestOptions> = {}
  ): Observable<ClientReferralMember[]> {
    const cachedMembers = this.membersSubject.value;

    // If not forcing refresh and we have cached data, return it
    if (!forceRefresh && cachedMembers.size > 0) {
      return this.members$.pipe(
        map((members) => Array.from(members.values())),
      );
    }

    // Otherwise, fetch from API
    this.isLoadingSubject.next(true);

    return this.clientReferralMembersHttpService.fetchAll(opts).pipe(
      tap((members) => {
        // Update the store with all fetched members
        const currentMembers = new Map(this.membersSubject.value);
        members.forEach((member) => {
          currentMembers.set(member.id, member);
        });
        this.membersSubject.next(currentMembers);
        this.isLoadingSubject.next(false);
      })
    );
  }

  /**
   * Archives a list of client referral members
   * @param crmIds client referral member ids
   * @param opts Optional request options
   * @returns Observable of all archived ClientReferralMembers
   */
  archive$(
    crmIds: number[],
    opts: Partial<IAPIFetchRequestOptions> = {}
  ): Observable<ClientReferralMember[]> {
    return this.clientReferralMembersHttpService.archive$(crmIds, opts).pipe(
      map((crms) => {
        if (!crms) return [] as ClientReferralMember[];
        this.updateMembersInCache(crms);
        return crms;
      })
    );
  }
}
